import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, CheckCircle2, Loader2, FileText, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { tr, guessLanguageFromText } from "../lib/i18n";
import { PublicLanguageToggle } from "../components/app/PublicLanguageToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicQuote() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [err, setErr] = useState(null);

  // PUBLIC-only language state (see PublicLeadForm for rationale). Priority:
  //   1. `?lang=en|es` query param (lets the owner share a pre-localised link
  //      via "Copy Spanish link" on Lead Detail) — wins over everything.
  //   2. Browser navigator.language if it starts with "es".
  //   3. Default English.
  // Once the quote loads we also sniff the quote text itself, but only if
  // the customer hasn't made an explicit choice yet (see `userPickedLang`).
  const [lang, setLang] = useState(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const qp = (qs.get("lang") || "").toLowerCase();
      if (qp === "en" || qp === "es") return qp;
      return (navigator?.language || "").toLowerCase().startsWith("es") ? "es" : "en";
    } catch {
      return "en";
    }
  });
  // Once the quote loads, flip lang to match the quote's own language (only
  // once, never override a user who already toggled). We track whether the
  // user has manually chosen a language so the auto-detect doesn't fight
  // them. If `?lang=` was provided, we also treat that as an explicit pick
  // so auto-detect-from-quote-text won't overwrite it.
  const [userPickedLang, setUserPickedLang] = useState(() => {
    try {
      const qp = new URLSearchParams(window.location.search).get("lang");
      return qp === "en" || qp === "es";
    } catch {
      return false;
    }
  });
  const pickLang = (v) => { setUserPickedLang(true); setLang(v); };

  const t = (key, ...args) => tr(key, lang, ...args);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quotes/public/${quoteId}`);
        setQuote(data);
        if (!userPickedLang) {
          const detected = guessLanguageFromText(
            [data?.price_text, data?.message].filter(Boolean).join("\n")
          );
          setLang(detected);
        }
      } catch {
        setErr("invalid");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId]);

  const accept = async () => {
    setAccepting(true);
    try {
      const { data } = await axios.post(`${API}/quotes/public/${quoteId}/accept`);
      setQuote(data);
      toast.success(lang === "es" ? "¡Cotización aceptada!" : "Quote accepted!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("public.quote.acceptError"));
    } finally {
      setAccepting(false);
    }
  };

  // Owner-only preview mode — toggled via `?preview=1` on the URL. Shows a
  // banner up top and disables the Accept button so the owner can audit
  // exactly what the customer will see without risking an accidental
  // self-acceptance. The real customer URL (no query param) is unchanged.
  const isOwnerPreview = (() => {
    try {
      return new URLSearchParams(window.location.search).get("preview") === "1";
    } catch {
      return false;
    }
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9f8]">
        <Loader2 className="w-6 h-6 animate-spin text-green-800" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9f8] p-6" data-testid="quote-invalid">
        <div className="max-w-md text-center">
          <p className="font-display text-2xl font-semibold text-gray-900">{t("public.quote.invalidTitle")}</p>
          <p className="mt-2 text-gray-500">{t("public.quote.invalidBody")}</p>
          <Link to="/" className="mt-6 inline-block text-green-800 hover:underline">{t("public.form.gotoTasklio")}</Link>
        </div>
      </div>
    );
  }

  const accepted = quote.status === "accepted";

  // Render the scheduled date for the success card. We try `Intl` first so
  // "2026-06-01" becomes "June 1, 2026" (or "1 de junio de 2026" in ES).
  // Falls back to the raw string when the input isn't ISO-parseable —
  // owners sometimes type things like "next Friday afternoon" into the
  // preferred-date field, which we want to preserve verbatim.
  // Format a YYYY-MM-DD date string for display WITHOUT going through
  // Date()'s timezone parsing. `new Date("2026-06-15")` interprets the
  // string as UTC midnight, then `toLocaleDateString` renders in the
  // browser's local timezone — for a US Eastern (UTC-4) user that
  // shifts back to "June 14". Manual parse + Date(y, m, d) keeps the
  // date stable across all timezones.
  const formatScheduledDate = (raw) => {
    if (!raw || typeof raw !== "string") return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const isoOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (isoOnly) {
      const y = parseInt(isoOnly[1], 10);
      const m = parseInt(isoOnly[2], 10) - 1;
      const d = parseInt(isoOnly[3], 10);
      const local = new Date(y, m, d);
      if (!Number.isNaN(local.getTime())) {
        try {
          return local.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        } catch {
          return trimmed;
        }
      }
    }
    // ISO with time component (e.g. "2026-06-15T10:00") — safe to use
    // the date constructor because the time disambiguates the day.
    const isoFull = /^\d{4}-\d{2}-\d{2}T/.exec(trimmed);
    if (isoFull) {
      const d = new Date(trimmed);
      if (!Number.isNaN(d.getTime())) {
        try {
          return d.toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          });
        } catch {
          return trimmed;
        }
      }
    }
    // Free-form text like "next Friday" — leave verbatim.
    return trimmed;
  };
  const scheduledDate = formatScheduledDate(quote.scheduled_date || quote.preferred_date);
  // Time row on the accepted card — only renders when the server returned
  // a confirmed time label. Pre-formatted by the backend in the owner's
  // timezone so we display exactly what was agreed, never a browser-tz
  // re-interpretation.
  const scheduledTime = quote.scheduled_time_label || null;
  const acceptedPrice =
    typeof quote.suggested_price === "number" && !Number.isNaN(quote.suggested_price)
      ? `$${Math.round(quote.suggested_price).toLocaleString()}`
      : null;

  return (
    <div className="min-h-screen bg-[#f9f9f8]" data-testid="public-quote-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {isOwnerPreview ? (
          <div
            className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
            data-testid="owner-preview-banner"
          >
            <span className="text-lg leading-none">👀</span>
            <div className="min-w-0">
              <p className="font-semibold">{t("public.quote.previewTitle")}</p>
              <p className="mt-0.5 text-amber-800/90 leading-relaxed whitespace-pre-line">
                {t("public.quote.previewBody")}
              </p>
            </div>
          </div>
        ) : null}
        <div className="flex items-center justify-between mb-8 gap-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-lg">Tasklio AI</span>
          </div>
          <PublicLanguageToggle lang={lang} setLang={pickLang} testidPrefix="public-quote-lang-switch" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-10">
          {accepted ? (
            <div className="text-center" data-testid="quote-accepted-state">
              <div className="w-14 h-14 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-green-800" />
              </div>
              <h1
                className="font-display mt-5 text-3xl sm:text-4xl font-semibold text-gray-900"
                data-testid="accepted-title"
              >
                {t("public.quote.acceptedTitle")}
              </h1>
              <p
                className="mt-3 text-lg font-medium text-gray-900"
                data-testid="accepted-headline"
              >
                {t("public.quote.acceptedHeadline")}
              </p>
              <p className="mt-2 text-gray-600" data-testid="accepted-body">
                {t("public.quote.acceptedBody", quote.business_name)}
              </p>

              {/* Summary card — service / date / price. Each row hides
                  itself when the underlying field is missing so older
                  quotes still render cleanly. */}
              {(quote.service_type || scheduledDate || scheduledTime || acceptedPrice) ? (
                <div
                  className="mt-6 rounded-xl border border-green-100 bg-green-50/50 p-5 text-left space-y-2"
                  data-testid="accepted-summary"
                >
                  {quote.service_type ? (
                    <div
                      className="flex items-start gap-2 text-sm text-gray-800"
                      data-testid="accepted-summary-service"
                    >
                      <span className="text-green-700 shrink-0">✅</span>
                      <span>
                        <span className="font-medium">{t("public.quote.summaryService")}</span>{" "}
                        {quote.service_type}
                      </span>
                    </div>
                  ) : null}
                  {scheduledDate ? (
                    <div
                      className="flex items-start gap-2 text-sm text-gray-800"
                      data-testid="accepted-summary-date"
                    >
                      <span className="text-green-700 shrink-0">✅</span>
                      <span>
                        <span className="font-medium">{t("public.quote.summaryDate")}</span>{" "}
                        {scheduledDate}
                      </span>
                    </div>
                  ) : null}
                  {scheduledTime ? (
                    <div
                      className="flex items-start gap-2 text-sm text-gray-800"
                      data-testid="accepted-summary-time"
                    >
                      <span className="text-green-700 shrink-0">✅</span>
                      <span>
                        <span className="font-medium">{t("public.quote.summaryTime")}</span>{" "}
                        {scheduledTime}
                      </span>
                    </div>
                  ) : null}
                  {acceptedPrice ? (
                    <div
                      className="flex items-start gap-2 text-sm text-gray-800"
                      data-testid="accepted-summary-price"
                    >
                      <span className="text-green-700 shrink-0">✅</span>
                      <span>
                        <span className="font-medium">{t("public.quote.summaryPrice")}</span>{" "}
                        {acceptedPrice}
                      </span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <p
                className="mt-6 text-gray-600"
                data-testid="accepted-thanks"
              >
                {t("public.quote.acceptedThanks", quote.business_name)}
              </p>
            </div>
          ) : (
            <>
              <span className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("public.quote.eyebrow")}</span>
              <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
                {t("public.quote.greeting", quote.customer_name)}
              </h1>
              <p className="mt-2 text-gray-500">
                <span className="font-medium text-gray-700">{quote.business_name}</span>{" "}
                {t("public.quote.intro")}
              </p>

              <div className="mt-8 space-y-5">
                {quote.service_type && (
                  <div className="rounded-xl border border-gray-200 p-4" data-testid="quote-service">
                    <p className="text-xs text-gray-400">{t("public.quote.serviceLabel")}</p>
                    <p className="mt-1 text-gray-900 font-medium">{quote.service_type}</p>
                  </div>
                )}

                {/* Scheduled appointment — only rendered when the AI
                    quote extracted a confirmed date from the Step-1
                    reply (e.g. "Monday, June 8th at 3 pm"). Customer
                    sees the human-friendly label; the calendar inherits
                    the structured date+time on accept. Hidden entirely
                    when no schedule was confirmed — we never invent a
                    date or a time. */}
                {quote.scheduled_date ? (
                  <div
                    className="rounded-xl border border-sky-200 bg-sky-50/60 p-4 flex items-start gap-3"
                    data-testid="quote-scheduled-appointment"
                  >
                    <Calendar className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-sky-800">
                        {t("public.quote.scheduledLabel")}
                      </p>
                      <p className="mt-1 text-gray-900 font-medium" data-testid="quote-scheduled-text">
                        {formatScheduledDate(quote.scheduled_date) || quote.scheduled_date}
                        {quote.scheduled_time_label ? (
                          <>
                            {" "}
                            <span className="text-gray-500">{t("public.quote.scheduledAt")}</span>{" "}
                            <span className="font-semibold">{quote.scheduled_time_label}</span>
                          </>
                        ) : null}
                      </p>
                      {(() => {
                        // Long-form "Weekly Recurring Service" label.
                        // Prefer client-side translation (we own the
                        // language toggle) and fall back to the server-
                        // provided `frequency_label` for legacy quotes.
                        const f = (quote.frequency || "").toString().toLowerCase();
                        const tryKey = `public.quote.frequencyLong.${f}`;
                        const longLabel = t(tryKey);
                        const resolved = longLabel.startsWith("public.quote.frequencyLong.")
                          ? quote.frequency_label
                          : longLabel;
                        if (!resolved) return null;
                        return (
                          <p
                            className="mt-2 text-xs text-sky-900"
                            data-testid="quote-scheduled-frequency"
                          >
                            <span className="font-semibold uppercase tracking-wider text-sky-700/80">
                              {t("public.quote.frequencyLabel")}
                            </span>{" "}
                            <span className="text-gray-800">{resolved}</span>
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                ) : null}

                {/* Price block — prefer the structured `suggested_price`
                    (renders as "Estimated Quote: $XXX") and fall back to
                    legacy `price_text` for older quotes. NEVER expose
                    range / confidence / reasoning / next_best_action. */}
                {typeof quote.suggested_price === "number" && !Number.isNaN(quote.suggested_price) ? (
                  <div
                    className="rounded-xl bg-gradient-to-br from-green-50 to-sky-50 border border-green-100 p-6"
                    data-testid="quote-price"
                  >
                    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-green-800">
                      <FileText className="w-3.5 h-3.5" /> {t("public.quote.estimatedQuoteLabel")}
                    </div>
                    <p
                      className="font-display mt-2 text-4xl sm:text-5xl font-semibold text-gray-900"
                      data-testid="quote-suggested-price"
                    >
                      ${Math.round(quote.suggested_price).toLocaleString()}
                    </p>
                  </div>
                ) : quote.price_text ? (
                  <div className="rounded-xl bg-gradient-to-br from-green-50 to-sky-50 border border-green-100 p-5" data-testid="quote-price">
                    <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-green-800">
                      <FileText className="w-3.5 h-3.5" /> {t("public.quote.priceLabel")}
                    </div>
                    <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
{quote.price_text}
                    </pre>
                  </div>
                ) : null}

                {/* What's Included — structured checklist. Hidden entirely
                    when the array is missing/empty so older quotes still
                    render cleanly. */}
                {Array.isArray(quote.included) && quote.included.length > 0 ? (
                  <div
                    className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-5"
                    data-testid="quote-included"
                  >
                    <p className="text-xs tracking-[0.2em] uppercase font-semibold text-emerald-800">
                      {t("public.quote.includedLabel")}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {quote.included.map((item, idx) => (
                        <li
                          key={`inc-${idx}`}
                          className="flex items-start gap-2 text-sm text-gray-800 leading-relaxed"
                          data-testid={`quote-included-item-${idx}`}
                        >
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {/* Optional Add-ons intentionally HIDDEN from the public
                    quote page. They are still generated and stored on the
                    Quote document for future owner-facing features (e.g.
                    upsell suggestions inside Lead Detail), but exposing
                    "+$45 / +$35 / +$50" right next to the Accept button
                    hurts conversion — customers fixate on the upsells
                    instead of the actual estimate they just received.
                    See spec doc dated 2026-05 for full rationale. */}

                {/* Message from business — prefer the structured
                    `customer_message` (cleanest, AI-written customer-facing
                    copy) over the legacy `message`. */}
                {(quote.customer_message || quote.message) ? (
                  <div className="rounded-xl border border-gray-200 p-5" data-testid="quote-message">
                    <p className="text-xs text-gray-400">{t("public.quote.messageFrom", quote.business_name)}</p>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
{quote.customer_message || quote.message}
                    </pre>
                  </div>
                ) : null}
              </div>

              {isOwnerPreview ? (
                <div
                  className="mt-8 w-full rounded-md border border-amber-300 bg-amber-50 px-4 py-4 text-center"
                  data-testid="quote-accept-button-preview"
                >
                  <p className="text-sm font-semibold text-amber-900">
                    {t("public.quote.previewDisabledTitle")}
                  </p>
                  <p className="mt-1 text-xs text-amber-800/90 leading-relaxed">
                    {t("public.quote.previewDisabledBody")}
                  </p>
                </div>
              ) : (
                <Button
                  onClick={accept}
                  disabled={accepting}
                  className="mt-8 w-full h-12 bg-green-800 hover:bg-green-900 text-white text-base"
                  data-testid="quote-accept-button"
                >
                  {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("public.quote.accept")}
                </Button>
              )}
              <p
                className="mt-4 text-xs text-gray-400 leading-relaxed text-center"
                data-testid="quote-disclaimer"
              >
                {t("public.quote.disclaimer")}
              </p>
              <p className="mt-3 text-xs text-gray-400 text-center">
                {t("public.form.poweredBy")} <span className="font-medium text-gray-500">Tasklio AI</span>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
